import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  List,
  TextField,
  DateField,
  FunctionField,
  TopToolbar,
  useListContext,
  ExportButton,
  ReferenceField
} from 'react-admin';
import jsonExport from 'jsonexport/dist';

import FlexibleListLayout from '../FlexibleListLayout';
import YenField from '../YenField';
import downloadCSVCompat from '../../utils/downloadCSVCompat';
import { formatRFC3339DateOnlyString } from '../../utils/dateutils';
import { MAX_PER_PAGE } from '../../constants/define';

interface PaymentDetailItem {
  id: number;
  clientId: number;
  clientName?: string;
  partnerId: number;
  partnerName?: string;
  affiliateId?: string;
}

interface PaymentDetailItemsListProps {
  paymentId: number;
  apiBasePath?: string;
  title?: string;
  ecSiteLinkEnabled?: boolean;
  ecSiteUseReferenceField?: boolean;
  ecSiteReferenceKey?: string;
  partnerUseReferenceField?: boolean;
  partnerReferenceKey?: string;
  partnerSourceField?: string;
  partnerLinkBasePath?: string;
  showEcSiteColumns?: boolean;
}

export const PaymentDetailItemsList: React.FC<PaymentDetailItemsListProps> = ({
  paymentId,
  apiBasePath = 'payments',
  title = '支払い詳細項目',
  ecSiteLinkEnabled = true,
  ecSiteUseReferenceField = true,
  ecSiteReferenceKey = 'references/clients',
  partnerUseReferenceField = true,
  partnerReferenceKey = 'references/partners',
  partnerSourceField = 'partnerId',
  partnerLinkBasePath = '/partners',
  showEcSiteColumns = true
}) => {
  const handleExport = async (records: any[]) => {
    if (!records || records.length === 0) {
      return;
    }

    let exportFields = [
      'ecSiteOrderId',
      'ecSiteClientId',
      'clientId',
      'clientName',
      'partnerName',
      'affiliateId',
      'orderDate',
      'approvedAt',
      'rewardAmount'
    ];

    let headers = [
      'ECサイト注文ID',
      'ECサイト顧客ID',
      'ショップID',
      'ECサイト名',
      'パートナー名',
      'アフィリエイトID',
      '注文日時',
      '承認日時',
      '報酬額'
    ];

    if (!showEcSiteColumns) {
      const excludeFields = ['clientId', 'clientName'];
      const excludeIndices = exportFields
        .map((field, index) => excludeFields.includes(field) ? index : -1)
        .filter(index => index !== -1);
      exportFields = exportFields.filter((_, index) => !excludeIndices.includes(index));
      headers = headers.filter((_, index) => !excludeIndices.includes(index));
    }

    const dataToExport = records.map((record) => {
      const transformedRecord: Record<string, any> = {};

      exportFields.forEach((key, index) => {
        let value = record[key];

        if ((key === 'orderDate' || key === 'approvedAt') && value) {
          value = formatRFC3339DateOnlyString(value);
        }

        const numericFields = ['rewardAmount'];
        let finalValue;
        if (numericFields.includes(key)) {
          finalValue = value !== null && value !== undefined ? value.toLocaleString() : '';
        } else {
          finalValue = value || '';
        }
        transformedRecord[headers[index]] = finalValue;
      });

      return transformedRecord;
    });

    jsonExport(dataToExport, { headers }, (err: Error | null, csv: string) => {
      if (err) {
        console.error(err);
        return;
      }
      const bom = '\uFEFF';
      const csvWithBom = bom + csv;
      downloadCSVCompat(csvWithBom, title);
    });
  };

  const PaymentDetailItemsActions = ({ exporter }: { exporter?: (records: any[]) => void }) => {
    const { total } = useListContext();
    return (
      <TopToolbar>
        <ExportButton label="CSVダウンロード" exporter={exporter} disabled={total === 0} maxResults={MAX_PER_PAGE} />
      </TopToolbar>
    );
  };

  // ECサイト名の表示コンポーネント
  const renderEcSiteNameField = () => {
    if (ecSiteUseReferenceField) {
      // ReferenceFieldを使用（admin_web_server用）
      return (
        <ReferenceField
          source="clientId"
          reference={ecSiteReferenceKey}
          label="ECサイト名"
          sortBy="clientId"
          link={ecSiteLinkEnabled ? (record: any) => `/clients/${record.id}/show` : false}
        >
          <TextField source="name" />
        </ReferenceField>
      );
    } else if (ecSiteLinkEnabled) {
      // FunctionFieldでリンク表示
      return (
        <FunctionField
          source="clientName"
          label="ECサイト名"
          sortBy="clientId"
          render={(record: PaymentDetailItem) => (
            <a href={`#/clients/${record.clientId}/show`}>{record.clientName}</a>
          )}
        />
      );
    } else {
      // TextFieldで表示（client_web_server用）
      return <TextField source="clientName" label="ECサイト名" sortBy="clientId" />;
    }
  };

  // パートナー名の表示コンポーネント
  const renderPartnerNameField = () => {
    if (partnerUseReferenceField) {
      // ReferenceFieldを使用
      return (
        <ReferenceField
          source={partnerSourceField}
          reference={partnerReferenceKey}
          label="パートナー名"
          sortBy={partnerSourceField}
          link={(record: any) => `${partnerLinkBasePath}/${record.id}/show`}
        >
          <TextField source="name" />
        </ReferenceField>
      );
    } else {
      // FunctionFieldでリンク表示
      return (
        <FunctionField
          source="partnerName"
          label="パートナー名"
          sortBy={partnerSourceField}
          render={(record: PaymentDetailItem) => {
            const linkId = partnerSourceField === 'affiliateId' ? record.affiliateId : record.partnerId;
            return <a href={`#${partnerLinkBasePath}/${linkId}/show`}>{record.partnerName}</a>;
          }}
        />
      );
    }
  };

  return (
    <List
      resource={`${apiBasePath}/details/${paymentId}/list`}
      sort={{ field: 'approvedAt', order: 'ASC' }}
      title={title}
      actions={<PaymentDetailItemsActions exporter={handleExport} />}
      exporter={handleExport}
      empty={
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {title}はありません。
          </Typography>
        </Box>
      }
    >
      <FlexibleListLayout bulkActionButtons={false}>
        <TextField source="ecSiteOrderId" label="ECサイト注文ID" />
        <TextField source="ecSiteClientId" label="ECサイト顧客ID" />
        {showEcSiteColumns && <TextField source="clientId" label="ショップID" />}
        {showEcSiteColumns && renderEcSiteNameField()}
        {renderPartnerNameField()}
        <TextField source="affiliateId" label="アフィリエイトID" />
        <DateField source="orderDate" label="注文日時" showTime />
        <DateField source="approvedAt" label="承認日時" showTime />
        <YenField source="rewardAmount" label="報酬額" />
      </FlexibleListLayout>
    </List>
  );
};

export default PaymentDetailItemsList;
